import React, { useState, useEffect, useCallback, useRef } from 'react';
import { UserCircle, ChevronDown, Camera } from 'lucide-react';
import './App.css';
import LoginView from './components/LoginView';
import HomeFeedView from './components/HomeFeedView';
import RelativesList from './components/RelativesList';
import RecordTab from './components/RecordTab';
import RecordingView from './components/RecordingView';
import RelativeProgressView from './components/RelativeProgressView';
import VirtualChatView from './components/VirtualChatView';
import ProfileView from './components/ProfileView';
import WeeklyCalloutResponsesView from './components/WeeklyCalloutResponsesView';
import WeeklyCalloutsListView from './components/WeeklyCalloutsListView';
import FamilyProfileView from './components/FamilyProfileView';
import SubscriptionView from './components/SubscriptionView';
import SubscriptionStoreFrontView from './components/SubscriptionStoreFrontView';
import SubscriptionPaywallView from './components/SubscriptionPaywallView';
import Toast from './components/Toast';
import AuthService from './services/AuthService';
import ApiService from './services/ApiService';

function App() {
  const [user, setUser] = useState(null);
  const [relatives, setRelatives] = useState([]);
  const [families, setFamilies] = useState([]);
  const [currentView, setCurrentView] = useState('home');
  const [viewData, setViewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [toast, setToast] = useState(null);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
  const [recordingData, setRecordingData] = useState(null); // { relative, mode, calloutQuestion }
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  // Content cache: { [relativeUuid]: { contents, favorites, timestamp } }
  const contentCacheRef = useRef({});
  // Lightweight manifest of all content UUIDs (from /checkForContentList)
  const [contentManifest, setContentManifest] = useState(null);
  // Resolved full content previews (from /checkForContentBatch), keyed for quick lookup
  const [homeFeedContent, setHomeFeedContent] = useState(null);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#subscription-success') {
      window.location.hash = '';
      showToast('Processing your subscription...', 'success');
      // Wait for webhook to process, refresh token, then verify subscription is active
      setTimeout(async () => {
        try {
          await AuthService.refreshAccessToken();
        } catch (e) { /* token may still be valid */ }
        const subInfo = await loadSubscriptionInfo();
        if (subInfo?.subscriptionValid) {
          showToast('Subscription activated!', 'success');
          await loadFamiliesAndRelatives();
        } else {
          showToast('Subscription is still processing. Please refresh in a moment.', 'error');
        }
        setLoading(false);
      }, 3000);
    } else if (hash === '#subscription-cancel') {
      window.location.hash = '';
      showToast('Purchase cancelled', 'error');
      checkAuthentication();
    } else {
      checkAuthentication();
    }

    // Register 401/403 handler — redirect to login
    ApiService.onAuthFailure = () => {
      AuthService.logout();
      setUser(null);
      setRelatives([]);
      setFamilies([]);
      setCurrentView('home');
      setActiveTab('home');
    };
    // Register 402 handler — redirect to subscription storefront
    ApiService.onSubscriptionRequired = () => {
      setCurrentView('subscriptionStore');
      setToast({ message: 'A subscription is required to continue', type: 'error' });
    };
    return () => { ApiService.onSubscriptionRequired = null; ApiService.onAuthFailure = null; };
  }, []);

  const loadSubscriptionInfo = async () => {
    try {
      const data = await ApiService.getSubscriptionInfo();
      setSubscriptionInfo(data);
      return data;
    } catch (error) {
      console.error('Failed to load subscription info:', error);
      return null;
    }
  };

  const showToast = useCallback((message, type = 'error') => {
    setToast({ message, type });
  }, []);

  const dismissToast = useCallback(() => {
    setToast(null);
  }, []);

  const checkAuthentication = async () => {
    if (AuthService.isAuthenticated()) {
      try {
        // Subscription info is auth-only (no subscription check required).
        // It also auto-creates a trial for new users.
        const subInfo = await loadSubscriptionInfo();

        if (subInfo?.subscriptionValid) {
          // Subscription is good — load family data (subscription-protected route)
          await loadFamiliesAndRelatives();
        } else {
          // Subscription expired/missing — set minimal user so paywall can show
          const userUuid = localStorage.getItem('userUuid');
          if (userUuid) {
            setUser({ uuid: userUuid, name: '', profilePictureUrl: null, birthDate: null });
          }
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        AuthService.logout();
      }
    }
    setLoading(false);
  };

  const loadFamiliesAndRelatives = async () => {
    try {
      const data = await ApiService.getFamiliesAndRelatives();
      const relArray = data.relativeArray || [];
      const famArray = data.familyArray || [];
      setRelatives(relArray);
      setFamilies(famArray);

      // Auto-select first family if not already set
      if (famArray.length > 0) {
        setSelectedFamily(prev => prev || famArray[0]);
      }

      // User's name is on each family object; profile picture and birthday
      // are on the relative entry that matches the current user's UUID.
      const userUuid = localStorage.getItem('userUuid');
      const selfRelative = relArray.find(r => r.uuid === userUuid);
      setUser({
        uuid: userUuid,
        name: famArray[0]?.userName || localStorage.getItem('userName') || '',
        profilePictureUrl: selfRelative?.profilePictureUrl || null,
        birthDate: selfRelative?.birthDate || null,
      });
    } catch (error) {
      console.error('Failed to load data:', error);
      throw error;
    }
  };

  const getContentCache = useCallback((relativeUuid) => {
    return contentCacheRef.current[relativeUuid] || null;
  }, []);

  const setContentCache = useCallback((relativeUuid, data) => {
    contentCacheRef.current[relativeUuid] = { ...data, timestamp: Date.now() };
  }, []);

  // Filter relatives by selected family
  const filteredRelatives = selectedFamily && families.length > 1
    ? relatives.filter(r => (r.families || []).some(f => f.uuid === selectedFamily.uuid))
    : relatives;

  const handleFamilyChange = (family) => {
    setSelectedFamily(family);
    setShowFamilyDropdown(false);
    // Clear content cache so HomeFeedView reloads for new family
    setContentManifest(null);
    setHomeFeedContent(null);
  };

  const handleLogin = async () => {
    const subInfo = await loadSubscriptionInfo();
    if (subInfo?.subscriptionValid) {
      await loadFamiliesAndRelatives();
    } else {
      // Subscription not valid — set minimal user so paywall shows
      const userUuid = localStorage.getItem('userUuid');
      if (userUuid) {
        setUser({ uuid: userUuid, name: '', profilePictureUrl: null, birthDate: null });
      }
    }
  };

  const handleLogout = () => {
    AuthService.logout();
    setUser(null);
    setRelatives([]);
    setFamilies([]);
    setCurrentView('home');
    setActiveTab('home');
    setViewData(null);
    setSelectedFamily(null);
    setShowFamilyDropdown(false);
    contentCacheRef.current = {};
    setContentManifest(null);
    setHomeFeedContent(null);
  };

  const handleSelectRelative = (relative) => {
    setViewData(relative);
    setCurrentView('progress');
  };

  const handleOpenChat = (relative) => {
    setViewData(relative);
    setCurrentView('chat');
  };

  const handleOpenProfile = () => {
    setCurrentView('profile');
  };

  const handleOpenCalloutResponses = (callout) => {
    setViewData(callout);
    setCurrentView('calloutResponses');
  };

  const handleOpenCalloutsHistory = () => {
    setCurrentView('calloutsHistory');
  };

  const handleSelectFamily = (family) => {
    setViewData(family);
    setCurrentView('familyProfile');
  };

  const handleOpenSubscription = () => {
    setCurrentView('subscription');
  };

  const handleOpenSubscriptionStore = () => {
    setCurrentView('subscriptionStore');
  };

  const handleStartRecording = (relative, mode, calloutQuestion) => {
    setRecordingData({ relative, mode, calloutQuestion });
    setCurrentView('recording');
  };

  const handleContentUploaded = () => {
    // Invalidate content caches so feeds refresh
    contentCacheRef.current = {};
    setContentManifest(null);
    setHomeFeedContent(null);
  };

  const handleBack = () => {
    setCurrentView(activeTab);
    setViewData(null);
    setRecordingData(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!user) {
    return <LoginView onLogin={handleLogin} />;
  }

  // Show paywall when subscription is invalid and family data hasn't loaded
  const subscriptionValid = subscriptionInfo?.subscriptionValid;
  if (!subscriptionValid && families.length === 0) {
    // Allow storefront view through so user can purchase
    if (currentView === 'subscriptionStore') {
      return (
        <>
          <SubscriptionStoreFrontView
            currentPlanId={subscriptionInfo?.planId}
            subscriptionActive={subscriptionInfo?.subscriptionValid}
            autoRenewStatus={subscriptionInfo?.autoRenewStatus}
            paymentSource={subscriptionInfo?.paymentSource}
            onBack={() => setCurrentView('home')}
            onShowToast={showToast}
          />
          {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
        </>
      );
    }
    return (
      <>
        <SubscriptionPaywallView
          onUpdateSubscription={handleOpenSubscriptionStore}
          onLogout={handleLogout}
          onShowToast={showToast}
        />
        {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
      </>
    );
  }

  if (currentView === 'progress' && viewData) {
    return (
      <>
        <RelativeProgressView
          relative={viewData}
          currentUserUuid={user.uuid}
          onBack={handleBack}
          onShowToast={showToast}
          contentCache={getContentCache(viewData.uuid)}
          onContentLoaded={(data) => setContentCache(viewData.uuid, data)}
          homeFeedContent={homeFeedContent}
          contentManifest={contentManifest}
          onRelativeUpdated={(updated) => setRelatives(prev => prev.map(r => r.uuid === updated.uuid ? { ...r, ...updated } : r))}
        />
        {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
      </>
    );
  }

  if (currentView === 'chat' && viewData) {
    return (
      <>
        <VirtualChatView
          relative={viewData}
          onBack={handleBack}
        />
        {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
      </>
    );
  }

  if (currentView === 'calloutsHistory') {
    return (
      <>
        <WeeklyCalloutsListView
          families={selectedFamily ? families.filter(f => f.uuid === selectedFamily.uuid) : families}
          allContent={homeFeedContent}
          relatives={relatives}
          onBack={handleBack}
          onSelectCallout={handleOpenCalloutResponses}
        />
        {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
      </>
    );
  }

  if (currentView === 'calloutResponses' && viewData) {
    return (
      <>
        <WeeklyCalloutResponsesView
          callout={viewData}
          allContent={homeFeedContent}
          contentManifest={contentManifest}
          relatives={relatives}
          currentUserUuid={user.uuid}
          onBack={handleBack}
          onShowToast={showToast}
        />
        {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
      </>
    );
  }

  if (currentView === 'familyProfile' && viewData) {
    return (
      <>
        <FamilyProfileView
          family={viewData}
          relatives={relatives}
          currentUserUuid={user.uuid}
          onBack={handleBack}
          onShowToast={showToast}
          onFamilyUpdated={loadFamiliesAndRelatives}
        />
        {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
      </>
    );
  }

  if (currentView === 'recording' && recordingData) {
    return (
      <>
        <RecordingView
          relative={recordingData.relative}
          mode={recordingData.mode}
          families={families}
          selectedFamily={selectedFamily}
          currentUserUuid={user.uuid}
          calloutQuestion={recordingData.calloutQuestion}
          onBack={handleBack}
          onContentUploaded={handleContentUploaded}
          onShowToast={showToast}
        />
        {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
      </>
    );
  }

  if (currentView === 'subscription') {
    return (
      <>
        <SubscriptionView
          relatives={relatives}
          onBack={handleBack}
          onShowToast={showToast}
          onOpenStoreFront={handleOpenSubscriptionStore}
        />
        {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
      </>
    );
  }

  if (currentView === 'subscriptionStore') {
    return (
      <>
        <SubscriptionStoreFrontView
          currentPlanId={subscriptionInfo?.planId}
          subscriptionActive={subscriptionInfo?.subscriptionValid}
          autoRenewStatus={subscriptionInfo?.autoRenewStatus}
          paymentSource={subscriptionInfo?.paymentSource}
          onBack={() => setCurrentView('subscription')}
          onShowToast={showToast}
        />
        {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
      </>
    );
  }

  if (currentView === 'profile') {
    return (
      <>
        <ProfileView
          user={user}
          families={families}
          relatives={relatives}
          onBack={handleBack}
          onLogout={handleLogout}
          onUserUpdated={loadFamiliesAndRelatives}
          onShowToast={showToast}
          onSelectFamily={handleSelectFamily}
          onOpenSubscription={handleOpenSubscription}
        />
        {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header with tabs */}
      <div className="bg-gray-800 px-4 pt-4 pb-0">
        <div className="flex justify-between items-center mb-3">
          {families.length > 1 ? (
            <div className="relative">
              <button
                onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                className="flex items-center gap-1.5 text-white hover:text-purple-300 transition-colors"
              >
                <span className="text-xl font-bold">{selectedFamily?.name || 'Family Ring'}</span>
                <ChevronDown className={`w-5 h-5 transition-transform ${showFamilyDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showFamilyDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowFamilyDropdown(false)} />
                  <div className="absolute top-full left-0 mt-1 bg-gray-700 rounded-xl shadow-xl border border-gray-600 py-1 z-50 min-w-[200px]">
                    {families.map(family => (
                      <button
                        key={family.uuid}
                        onClick={() => handleFamilyChange(family)}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                          selectedFamily?.uuid === family.uuid
                            ? 'text-purple-400 bg-purple-600/15 font-medium'
                            : 'text-white hover:bg-gray-600'
                        }`}
                      >
                        {family.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <h1 className="text-xl font-bold text-white">{families[0]?.name || 'Family Ring'}</h1>
          )}
          <button
            onClick={handleOpenProfile}
            className="text-white hover:text-gray-300 transition-colors"
            aria-label="Profile"
          >
            {user.profilePictureUrl ? (
              <img src={user.profilePictureUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <UserCircle size={28} />
            )}
          </button>
        </div>
        <div className="flex">
          <button
            onClick={() => { setActiveTab('home'); setCurrentView('home'); }}
            className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'home' ? 'text-purple-400 border-purple-400' : 'text-gray-500 border-transparent'
            }`}
          >
            Home
          </button>
          <button
            onClick={() => { setActiveTab('people'); setCurrentView('people'); }}
            className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'people' ? 'text-purple-400 border-purple-400' : 'text-gray-500 border-transparent'
            }`}
          >
            People
          </button>
          <button
            onClick={() => { setActiveTab('record'); setCurrentView('record'); }}
            className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-1 ${
              activeTab === 'record' ? 'text-purple-400 border-purple-400' : 'text-gray-500 border-transparent'
            }`}
          >
            <Camera className="w-4 h-4" />
            Record
          </button>
        </div>
      </div>

      {/* Content area — both views stay mounted to avoid refetching */}
      <div className={`flex-1 text-white overflow-y-auto ${activeTab === 'home' ? '' : 'hidden'}`}>
        <HomeFeedView
          relatives={filteredRelatives}
          families={families}
          selectedFamily={selectedFamily}
          currentUserUuid={user.uuid}
          onShowToast={showToast}
          cachedContent={homeFeedContent}
          onContentLoaded={setHomeFeedContent}
          contentManifest={contentManifest}
          onManifestLoaded={setContentManifest}
          onOpenCalloutResponses={handleOpenCalloutResponses}
          onOpenCalloutsHistory={handleOpenCalloutsHistory}
        />
      </div>
      <div className={`flex-1 text-white overflow-y-auto ${activeTab === 'people' ? '' : 'hidden'}`}>
        <RelativesList
          relatives={filteredRelatives}
          families={families}
          onSelectRelative={handleSelectRelative}
          onOpenChat={handleOpenChat}
          onSelectFamily={handleSelectFamily}
        />
      </div>
      <div className={`flex-1 text-white overflow-y-auto ${activeTab === 'record' ? '' : 'hidden'}`}>
        <RecordTab
          relatives={filteredRelatives}
          families={families}
          selectedFamily={selectedFamily}
          currentUserUuid={user.uuid}
          onStartRecording={handleStartRecording}
        />
      </div>

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
    </div>
  );
}

export default App;
